import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form as AntForm,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { authFetch } from '../../utils/api';
import { tokenManager } from '../../utils/tokenManager';
import { FormActions, FormField } from '../../shared/form';
import { BrandUpsertSchema, type BrandFormValues } from '../../schemas/brand';

type BrandStatus = 'active' | 'reserved' | 'disabled';

interface BrandFeatures {
  enableRegistration: boolean;
}

interface BrandRecord {
  id?: number;
  code: string;
  label: string;
  boStatus: BrandStatus;
  features: BrandFeatures;
  clientPackageRequired: boolean;
  clientBundleId?: string | null;
  androidApplicationId?: string | null;
  notes?: string | null;
  defaultMemberTierId?: number | null;
  defaultMemberTierName?: string | null;
}

interface TierOption {
  id: number;
  name: string;
}

const STATUS_OPTIONS = [
  { value: 'active', label: '啟用' },
  { value: 'reserved', label: '預留' },
  { value: 'disabled', label: '停用' },
];

function statusTag(status: BrandStatus) {
  const map = {
    active: { color: 'green', label: '啟用' },
    reserved: { color: 'gold', label: '預留' },
    disabled: { color: 'red', label: '停用' },
  } as const;
  const item = map[status] ?? map.reserved;
  return <Tag color={item.color}>{item.label}</Tag>;
}

function toPayload(values: BrandFormValues, includeDefaultMemberTier: boolean) {
  return {
    code: values.code,
    label: values.label,
    boStatus: values.boStatus,
    clientPackageRequired: values.clientPackageRequired,
    clientBundleId: values.clientBundleId,
    androidApplicationId: values.androidApplicationId,
    notes: values.notes,
    defaultVersion: values.defaultVersion,
    ...(includeDefaultMemberTier ? { defaultMemberTierId: values.defaultMemberTierId } : {}),
    features: {
      enableRegistration: values.enableRegistration,
      enableFreeTrial: false,
      enableAnonymousUpgrade: false,
    },
  };
}

export const BrandList: React.FC = () => {
  const allBrandAdmin = tokenManager.isAllBrandAdmin();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<BrandRecord[]>([]);
  const [editing, setEditing] = useState<BrandRecord | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [tierOptions, setTierOptions] = useState<TierOption[]>([]);
  // The editor can be opened for a second brand before the first tier request
  // returns. Only the newest request is allowed to update the modal options.
  const tierOptionsRequestRef = useRef(0);
  const form = useForm<BrandFormValues>({
    resolver: zodResolver(BrandUpsertSchema),
    mode: 'onBlur',
    defaultValues: {
      code: '',
      label: '',
      boStatus: 'reserved',
      clientPackageRequired: true,
      clientBundleId: '',
      androidApplicationId: '',
      notes: '',
      defaultVersion: '1.0.0',
      defaultMemberTierId: null,
      enableRegistration: true,
    },
  });

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/admin/brands');
      const result = await res.json();
      if (result.code === 0 && Array.isArray(result.data)) {
        setItems(result.data);
        tokenManager.setBrandCatalog(result.data);
      } else {
        message.error(result.msg || '品牌資料載入失敗');
      }
    } catch {
      message.error('品牌資料載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const fetchTiersForBrand = useCallback(async (brand: string) => {
    const tierOptionsRequestId = ++tierOptionsRequestRef.current;
    try {
      const res = await authFetch(
        `/api/admin/tiers?brand=${encodeURIComponent(brand)}`
      );
      const result = await res.json();
      if (tierOptionsRequestId !== tierOptionsRequestRef.current) {
        return;
      }
      if (result.code === 0 && Array.isArray(result.data)) {
        setTierOptions(
          result.data.map((tier: TierOption) => ({
            id: tier.id,
            name: tier.name,
          }))
        );
      } else {
        setTierOptions([]);
        message.error(result.msg || '方案資料載入失敗');
      }
    } catch {
      if (tierOptionsRequestId !== tierOptionsRequestRef.current) {
        return;
      }
      setTierOptions([]);
      message.error('方案資料載入失敗');
    }
  }, []);

  const openCreate = () => {
    tierOptionsRequestRef.current += 1;
    setEditing(null);
    setTierOptions([]);
    form.reset({
      code: '',
      label: '',
      boStatus: 'reserved',
      clientPackageRequired: true,
      clientBundleId: '',
      androidApplicationId: '',
      notes: '',
      defaultVersion: '1.0.0',
      defaultMemberTierId: null,
      enableRegistration: true,
    });
    setModalOpen(true);
  };

  const openEdit = (record: BrandRecord) => {
    setTierOptions([]);
    setEditing(record);
    form.reset({
      code: record.code,
      label: record.label,
      boStatus: record.boStatus,
      clientPackageRequired: record.clientPackageRequired,
      clientBundleId: record.clientBundleId ?? undefined,
      androidApplicationId: record.androidApplicationId ?? undefined,
      notes: record.notes ?? undefined,
      defaultMemberTierId: record.defaultMemberTierId ?? null,
      defaultVersion: '1.0.0',
      enableRegistration: record.features.enableRegistration,
    });
    setModalOpen(true);
    void fetchTiersForBrand(record.code);
  };

  const save = async (values: BrandFormValues) => {
    const payload = toPayload(values, Boolean(editing));
    const url = editing
      ? `/api/admin/brands/${encodeURIComponent(editing.code)}`
      : '/api/admin/brands';
    const method = editing ? 'PUT' : 'POST';
    const res = await authFetch(url, {
      method,
      body: JSON.stringify(payload),
    });
    const result = await res.json();
    if (result.code !== 0) {
      message.error(result.msg || '儲存失敗');
      return;
    }
    message.success(editing ? '品牌已更新' : '品牌已建立');
    setModalOpen(false);
    form.reset();
    fetchBrands();
  };

  const closeModal = () => {
    tierOptionsRequestRef.current += 1;
    setModalOpen(false);
    form.reset();
  };

  if (!allBrandAdmin) {
    return (
      <Card>
        <Alert
          type="info"
          showIcon
          message="品牌註冊表只開放全品牌管理員"
          description="品牌管理員由總後台分配品牌範圍。"
        />
      </Card>
    );
  }

  return (
    <div className="page-enter">
      <Card
        title="品牌註冊表"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新增品牌
          </Button>
        }
      >
        <Alert
          showIcon
          type="info"
          style={{ marginBottom: 16 }}
          message="總後台先管理品牌註冊與權限邊界"
          description="新增品牌會建立品牌代碼、功能開關、預設等級與各端版本空殼；client 端可用獨立品牌包帶入品牌代碼。"
        />
        <Table
          rowKey="code"
          loading={loading}
          dataSource={items}
          pagination={false}
          scroll={{ x: 1000 }}
          columns={[
            {
              title: '品牌',
              key: 'brand',
              width: 220,
              render: (_, record) => (
                <Space direction="vertical" size={0}>
                  <Typography.Text strong>{record.label}</Typography.Text>
                  <Typography.Text type="secondary">{record.code}</Typography.Text>
                </Space>
              ),
            },
            {
              title: '狀態',
              dataIndex: 'boStatus',
              width: 110,
              render: statusTag,
            },
            {
              title: '帳號策略',
              key: 'features',
              width: 140,
              render: (_, record) => (
                <Space wrap>
                  <Tag color={record.features.enableRegistration ? 'green' : 'red'}>
                    {record.features.enableRegistration ? '可註冊' : '不註冊'}
                  </Tag>
                </Space>
              ),
            },
            {
              title: '自主註冊預設方案',
              key: 'defaultMemberTier',
              width: 220,
              render: (_, record) =>
                record.defaultMemberTierId != null && record.defaultMemberTierName ? (
                  <Space direction="vertical" size={0}>
                    <Typography.Text strong>{record.defaultMemberTierName}</Typography.Text>
                    <Typography.Text type="secondary">Tier #{record.defaultMemberTierId}</Typography.Text>
                  </Space>
                ) : (
                  <Typography.Text type="secondary">未設定</Typography.Text>
                ),
            },
            {
              title: '品牌包',
              key: 'package',
              width: 240,
              render: (_, record) => (
                <Space direction="vertical" size={0}>
                  <Tag color={record.clientPackageRequired ? 'blue' : 'default'}>
                    {record.clientPackageRequired ? '獨立品牌包' : '共用包'}
                  </Tag>
                  <Typography.Text type="secondary">
                    {record.clientBundleId || record.androidApplicationId || '-'}
                  </Typography.Text>
                </Space>
              ),
            },
            {
              title: '操作',
              key: 'actions',
              width: 100,
              render: (_, record) => (
                <Button size="small" onClick={() => openEdit(record)}>
                  編輯
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editing ? `編輯品牌：${editing.label}` : '新增品牌'}
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnHidden
        width={640}
      >
        <FormProvider {...form}>
          <AntForm layout="vertical" onFinish={form.handleSubmit(save)}>
            <FormField<BrandFormValues>
              name="code"
              label="品牌代碼"
              required
              help="2-16 位小寫英數或底線，且以字母開頭。"
              control={<Input disabled={!!editing} placeholder="example_brand" />}
            />
            <FormField<BrandFormValues>
              name="label"
              label="品牌名稱"
              required
              control={<Input placeholder="顯示在總後台與品牌包的名稱" />}
            />
            <FormField<BrandFormValues>
              name="boStatus"
              label="後台狀態"
              required
              control={<Select options={STATUS_OPTIONS} />}
            />
            {!editing ? (
              <>
                <FormField<BrandFormValues>
                  name="defaultVersion"
                  label="初始版本號"
                  control={<Input placeholder="1.0.0" />}
                />
                <Alert
                  showIcon
                  type="info"
                  message="自主註冊預設方案"
                  description="建立時系統會自動建立此品牌的「預設方案」，並設定為自主註冊預設方案；建立後可在編輯品牌中調整或清除。"
                />
              </>
            ) : (
              <FormField<BrandFormValues>
                name="defaultMemberTierId"
                label="自主註冊預設方案"
                help="只可選擇此品牌的方案；清除即表示尚未設定。"
                control={
                  <Select
                    allowClear
                    options={tierOptions.map(tier => ({
                      value: tier.id,
                      label: tier.name,
                    }))}
                  />
                }
              />
            )}
            <FormField<BrandFormValues>
              name="clientPackageRequired"
              label="需要獨立品牌包"
              valuePropName="checked"
              control={<Switch checkedChildren="需要" unCheckedChildren="不需要" />}
            />
            <FormField<BrandFormValues>
              name="clientBundleId"
              label="iOS Bundle ID"
              control={<Input placeholder="com.company.brand" />}
            />
            <FormField<BrandFormValues>
              name="androidApplicationId"
              label="Android Application ID"
              control={<Input placeholder="com.company.brand" />}
            />
            <FormField<BrandFormValues>
              name="enableRegistration"
              label="允許註冊"
              valuePropName="checked"
              control={<Switch />}
            />
            <FormField<BrandFormValues>
              name="notes"
              label="備註"
              control={<Input.TextArea rows={3} />}
            />
            <FormActions
              onCancel={closeModal}
              submitting={form.formState.isSubmitting}
              dirty={form.formState.isDirty}
              submitLabel="儲存"
            />
          </AntForm>
        </FormProvider>
      </Modal>
    </div>
  );
};
